<template>
  <div v-if="loading || !permsLoaded" class="flex items-center justify-center py-20">
    <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
  </div>
  <div v-else-if="canSubmit || user?.is_superuser" class="p-6 dark:text-gray-50">
    <div class="flex space-x-3 mb-6 items-center">
      <h1 class="text-2xl font-semibold dark:text-white">{{ isEdit ? 'Edit' : 'Add' }} Network Device{{ isMultiSubmit ? ' (Optional)' : '' }}</h1>
      <div class="relative group">
        <button
          type="button"
          class="bg-transparent hover:bg-transparent"
          @click="startTutorial"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-circle-question-mark dark:text-gray-50 text-gray-900"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg>
        </button>
        <span class="absolute top-full text-xs right-1/2 -translate-y-1/2 mb-1 px-2 py-0.5 glass-card opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">Tutorial</span>
      </div>
    </div>

    <div v-if="error" class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
      <strong class="font-bold">Error!</strong> <span class="block sm:inline">{{ error }}</span>
    </div>
    <form @submit.prevent="submitForm" class="space-y-6 glass-card p-6">
      <!-- 1. Core -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div class="flex flex-col">
          <div class="mb-1 flex items-center gap-1.5">
            <label for="name" class="font-medium dark:text-white">Device Name<span class="pl-1 text-red-500">*</span></label>
            <FieldHelp text="Name to identify this network device." />
          </div>
          <input id="name" v-model="form.name" type="text" placeholder="e.g. core-switch-01" @focus="clearFieldError('name')"
            :class="inputClass('name')" />
          <FieldError :message="fieldErrors.name" />
        </div>
        <div class="flex flex-col">
          <div class="mb-1 flex items-center gap-1.5">
            <label for="target" class="font-medium dark:text-white">Target Destination (IP / FQDN)<span class="pl-1 text-red-500">*</span></label>
            <FieldHelp text="IP address or FQDN of the device being monitored." />
          </div>
          <input id="target" v-model="form.target" type="text" placeholder="e.g. 10.0.0.1 or router.example.com" @focus="clearFieldError('target')"
            :class="inputClass('target')" />
          <FieldError :message="fieldErrors.target" />
        </div>
        <div v-if="user?.is_staff" class="flex flex-col">
          <div class="mb-1 flex items-center gap-1.5">
            <label class="font-medium dark:text-white">Scrape Interval / Timeout (s)</label>
            <FieldHelp text="How often the device is polled, and how long to wait before timing out (seconds). Timeout must not exceed the interval." />
          </div>
          <div class="grid grid-cols-2 gap-2">
            <input v-model.number="form.scrape_interval" type="number" placeholder="120" class="px-3 py-2 text-base sm:text-sm rounded-md focus:ring-2 focus:ring-blue-500" />
            <input v-model.number="form.scrape_timeout" type="number" placeholder="60" class="px-3 py-2 text-base sm:text-sm rounded-md focus:ring-2 focus:ring-blue-500" />
          </div>
          <FieldError :message="fieldErrors.scrape_timeout" />
        </div>
      </div>

      <!-- 2. Anchor (Site/Probe) + Tech Type -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6 border-t border-gray-200 dark:border-gray-700 pt-5">
        <div class="flex flex-col">
          <div class="flex items-center justify-between mb-1">
            <div class="flex items-center gap-1.5">
              <label class="font-medium dark:text-white">{{ anchor === 'site' ? 'Site' : 'Probe' }}<span class="pl-1 text-red-500">*</span></label>
              <FieldHelp text="Anchor the device to a site, or use the toggle to anchor it to a probe instead. One is required." />
            </div>
            <div v-if="showAnchorToggle" class="inline-flex rounded overflow-hidden border border-gray-300 dark:border-gray-600 text-xs">
              <button type="button" @click="anchor = 'site'" class="px-2.5 py-0.5" :class="anchor === 'site' ? 'bg-blue-600 text-white' : 'bg-transparent text-gray-500 dark:text-gray-400'">Site</button>
              <button type="button" @click="anchor = 'probe'" class="px-2.5 py-0.5" :class="anchor === 'probe' ? 'bg-blue-600 text-white' : 'bg-transparent text-gray-500 dark:text-gray-400'">Probe</button>
            </div>
          </div>
          <GlassSelect v-if="anchor === 'site'" id="site" v-model="form.site" :options="sites" :searchable="true" search-placeholder="Search sites" :error="!!fieldErrors.site" @change="clearFieldError('site')" />
          <GlassSelect v-else id="probe" v-model="form.probe" :options="probes" :searchable="true" search-placeholder="Search probes" :error="!!fieldErrors.probe" @change="clearFieldError('probe')" />
          <FieldError :message="fieldErrors.site || fieldErrors.probe" />
        </div>
        <div class="flex flex-col">
          <div class="mb-1 flex items-center gap-1.5">
            <label for="device_type" class="font-medium dark:text-white">Device Type<span class="pl-1 text-red-500">*</span></label>
            <FieldHelp text="The kind of device: switch, router, firewall, etc." />
          </div>
          <GlassSelect id="device_type" v-model="form.device_type" :options="deviceTypes" :valueKey="'value'" :labelKey="'label'" placeholder="Select a Device Type" :error="!!fieldErrors.device_type" @change="clearFieldError('device_type')" />
          <FieldError :message="fieldErrors.device_type" />
        </div>
        <div class="flex flex-col">
          <div class="mb-1 flex items-center gap-1.5">
            <label for="tech" class="font-medium dark:text-white">Tech Type<span class="pl-1 text-red-500">*</span></label>
            <FieldHelp text="The device's technology/vendor. Determines which metrics methods are available." />
          </div>
          <GlassSelect id="tech" v-model="tech" :options="techOptions" :valueKey="'value'" :labelKey="'label'" placeholder="Select a Tech Type" :error="!!fieldErrors.tech_type" @change="onTechChange" />
          <FieldError :message="fieldErrors.tech_type" />
        </div>
        <div class="flex items-center gap-1.5 mt-6">
          <label class="flex w-fit items-center gap-2"><input type="checkbox" v-model="form.in_maintenance" class="h-5 w-5" /> In maintenance</label>
          <FieldHelp text="Pause alerting while the device is under maintenance." />
        </div>
      </div>

      <!-- 4. Metrics collection (revealed once a Tech Type is chosen) -->
      <div v-if="tech" class="flex flex-col border-t border-gray-200 dark:border-gray-700 pt-5">
        <div class="flex items-center gap-1.5">
          <label id="collect-metrics-toggle" class="flex w-fit items-center gap-2 font-medium dark:text-white">
            <input type="checkbox" v-model="metricsEnabled" class="h-5 w-5" @change="onMetricsToggle" /> Collect metrics
          </label>
          <FieldHelp text="Gather metrics from the device via SNMP, API, or Fabric." />
        </div>
        <div v-if="metricsEnabled" id="metrics-method-group" class="mt-3">
          <div class="mb-1 flex items-center gap-1.5">
            <label class="font-medium dark:text-white">Metrics Method<span class="pl-1 text-red-500">*</span></label>
            <FieldHelp text="How metrics are collected. Available options depend on the tech type." />
          </div>
          <div class="flex flex-wrap gap-2">
            <button v-for="monitoringMethod in supportedMethods" :key="monitoringMethod" :id="'method-' + monitoringMethod" type="button" @click="selectMethod(monitoringMethod)"
              class="px-4 py-2 rounded-md text-sm font-semibold uppercase tracking-wide"
              :class="methodClass(monitoringMethod)">{{ monitoringMethod }}</button>
          </div>
        </div>
        <template v-if="isEdit && savedMethod">
          <div v-if="(method && method !== savedMethod) || !metricsEnabled" class="mt-2 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-300">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd" /></svg>
            <span v-if="metricsEnabled && method">Live monitoring via {{ methodLabel(savedMethod) }}. Saving replaces {{ methodLabel(savedMethod) }} with {{ methodLabel(method) }}.</span>
            <span v-else>Live monitoring via {{ methodLabel(savedMethod) }}. Saving removes the {{ methodLabel(savedMethod) }} configuration.</span>
          </div>
          <p v-else-if="method === savedMethod" class="mt-2 text-xs text-gray-500 dark:text-gray-400">Live monitoring via {{ methodLabel(savedMethod) }}.</p>
        </template>
        <FieldError :message="fieldErrors.method" />
      </div>

      <!-- Branch: SNMP -->
      <div v-if="method === 'snmp'" class="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/40 p-4 space-y-4">
        <div class="text-sm font-semibold text-gray-700 dark:text-gray-200">SNMP - poll device MIBs</div>
        <div class="flex flex-col">
          <div class="mb-1 flex items-center gap-1.5">
            <label class="font-medium dark:text-white">SNMP auth profile<span class="pl-1 text-red-500">*</span></label>
            <FieldHelp text="Credentials used to poll the device (v2c community or v3 user). Create one with + New." />
          </div>
          <div class="flex gap-2">
            <div class="flex-1">
              <GlassSelect id="snmp-auth-profile" v-model="snmp.authProfile" :options="authProfileOptions" :valueKey="'value'" :labelKey="'label'" placeholder="Select an auth profile" :searchable="true" search-placeholder="Search auth profiles" :error="!!fieldErrors.auth" @focus="clearFieldError('auth')" @change="onAuthChange" />
            </div>
            <button type="button" @click="openNewAuthProfileForm" class="btn bg-blue-100 text-blue-700 self-start whitespace-nowrap">+ New</button>
          </div>
          <FieldError :message="fieldErrors.auth" />
        </div>

        <!-- inline create profile -->
        <div v-if="showNewAuthProfileForm" class="rounded-md border border-dashed border-gray-300 dark:border-gray-600 p-4 space-y-3 bg-white/60 dark:bg-gray-800/60">
          <div class="font-medium text-sm">New auth profile</div>
          <div class="flex flex-col"><label class="text-xs font-semibold mb-1">Profile name</label><input v-model="profileDraft.name" type="text" placeholder="e.g. edge_v3" @keydown.enter.prevent class="px-3 py-2 text-base sm:text-sm rounded-md" /></div>
          <div class="inline-flex rounded-md overflow-hidden border border-gray-300 dark:border-gray-600">
            <button type="button" @click="profileDraft.ver = 2" class="px-3 py-1.5 text-sm" :class="profileDraft.ver === 2 ? 'bg-blue-600 text-white' : 'bg-transparent text-gray-600 dark:text-gray-300'">v2c</button>
            <button type="button" @click="profileDraft.ver = 3" class="px-3 py-1.5 text-sm" :class="profileDraft.ver === 3 ? 'bg-blue-600 text-white' : 'bg-transparent text-gray-600 dark:text-gray-300'">v3</button>
          </div>
          <div v-if="profileDraft.ver === 2" class="flex flex-col"><label class="text-xs font-semibold mb-1">Community</label><input v-model="profileDraft.community" type="text" placeholder="e.g. public" @keydown.enter.prevent class="px-3 py-2 text-base sm:text-sm rounded-md" /></div>
          <template v-else>
            <div class="flex flex-col"><label class="text-xs font-semibold mb-1">Username</label><input v-model="profileDraft.username" type="text" placeholder="e.g. snmp_user" @keydown.enter.prevent class="px-3 py-2 text-base sm:text-sm rounded-md" /></div>
            <div class="flex flex-col"><label class="text-xs font-semibold mb-1">Security level</label>
              <GlassSelect v-model="profileDraft.security_level" :options="secLevelOptions" :valueKey="'value'" :labelKey="'label'" /></div>
            <template v-if="profileDraft.security_level !== 'noAuthNoPriv'">
              <label class="flex w-fit items-center gap-2 text-xs text-gray-500 dark:text-gray-400"><input type="checkbox" v-model="showSecrets" class="h-4 w-4" /> Show passwords</label>
              <div class="grid grid-cols-2 gap-2">
                <div class="flex flex-col"><label class="text-xs font-semibold mb-1">Auth protocol</label><GlassSelect v-model="profileDraft.auth_protocol" :options="authProtoOptions" :valueKey="'value'" :labelKey="'label'" /></div>
                <div class="flex flex-col"><label class="text-xs font-semibold mb-1">Auth password</label><input v-model="profileDraft.auth_password" :type="showSecrets ? 'text' : 'password'" placeholder="SNMPv3 auth passphrase" @keydown.enter.prevent class="px-3 py-2 text-base sm:text-sm rounded-md" /></div>
              </div>
            </template>
            <template v-if="profileDraft.security_level === 'authPriv'">
              <div class="grid grid-cols-2 gap-2">
                <div class="flex flex-col"><label class="text-xs font-semibold mb-1">Privacy protocol</label><GlassSelect v-model="profileDraft.priv_protocol" :options="privProtoOptions" :valueKey="'value'" :labelKey="'label'" /></div>
                <div class="flex flex-col"><label class="text-xs font-semibold mb-1">Privacy password</label><input v-model="profileDraft.priv_password" :type="showSecrets ? 'text' : 'password'" placeholder="SNMPv3 privacy passphrase" @keydown.enter.prevent class="px-3 py-2 text-base sm:text-sm rounded-md" /></div>
              </div>
            </template>
          </template>
          <div class="flex gap-2">
            <button type="button" @click="createProfile" :disabled="!profileDraft.name.trim() || creatingProfile" class="btn bg-blue-600 text-white disabled:opacity-50">Create &amp; use profile</button>
            <button type="button" @click="showNewAuthProfileForm = false" class="btn bg-gray-300 text-gray-700 hover:bg-gray-400">Cancel</button>
          </div>
        </div>

        <div class="flex flex-col">
          <div class="mb-1 flex items-center gap-1.5">
            <label class="font-medium dark:text-white">SNMP metric profile (walk-OID set)<span class="pl-1 text-red-500">*</span></label>
            <FieldHelp text="The set of walk-OIDs collected from the device. Create one with + New." />
          </div>
          <div class="flex gap-2">
            <div class="flex-1">
              <GlassSelect id="snmp-metric-profile" v-model="snmp.metricProfile" :options="metricProfileOptions" :valueKey="'value'" :labelKey="'label'" placeholder="Select a metric profile" :searchable="true" search-placeholder="Search metric profiles" :error="!!fieldErrors.snmp_metric_profile" @focus="clearFieldError('snmp_metric_profile')" @change="onMetricProfileChange" />
            </div>
            <button type="button" @click="openNewMetricProfileForm" class="btn bg-blue-100 text-blue-700 self-start whitespace-nowrap">+ New</button>
          </div>
          <FieldError :message="fieldErrors.snmp_metric_profile" />
          <div v-if="selectedProfileOids.length" class="flex flex-wrap gap-2 mt-2">
            <span v-for="walk in selectedProfileOids" :key="walk.oid" :title="walk.oid" class="glass-card-lite rounded px-2 py-0.5 text-sm font-mono">{{ walkLabel(walk) }}</span>
          </div>
          <p v-else-if="snmp.metricProfile" class="mt-2 text-xs text-gray-500 dark:text-gray-400">This profile has no walk OIDs defined.</p>
        </div>

        <!-- inline create metric profile -->
        <div v-if="showNewMetricProfileForm" class="rounded-md border border-dashed border-gray-300 dark:border-gray-600 p-4 space-y-3 bg-white/60 dark:bg-gray-800/60">
          <div class="font-medium text-sm">New metric profile</div>
          <div class="flex flex-col"><label class="text-xs font-semibold mb-1">Profile name</label><input v-model="metricProfileDraft.name" type="text" placeholder="e.g. cisco_routers" @keydown.enter.prevent class="px-3 py-2 text-base sm:text-sm rounded-md" /></div>
          <div class="flex flex-col">
            <label class="text-xs font-semibold mb-1">Walk OIDs</label>
            <div class="flex gap-2">
              <input v-model="oidInput" type="text" placeholder="add a numeric OID e.g. 1.3.6.1.2.1.31.1.1" @focus="clearFieldError('walk_oid')" :class="['flex-1', inputClass('walk_oid')]" @keydown.enter.prevent="addOid" />
              <button type="button" @click="addOid" class="btn bg-blue-100 text-blue-700">Add</button>
            </div>
            <FieldError :message="fieldErrors.walk_oid" />
            <div class="flex flex-wrap gap-2 mt-2">
              <div v-for="(walk, index) in metricProfileDraft.oids" :key="walk.oid + index" class="flex items-center gap-1 glass-card-lite rounded px-2 py-0.5">
                <span class="text-sm font-mono">{{ walkLabel(walk) }}</span>
                <button type="button" @click="metricProfileDraft.oids.splice(index, 1)" aria-label="Remove OID" title="Remove OID"
                  class="bg-transparent hover:bg-transparent p-0 text-red-500 hover:text-red-700 dark:hover:text-red-400">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" /></svg>
                </button>
              </div>
            </div>
          </div>
          <div class="flex gap-2">
            <button type="button" @click="createMetricProfile" :disabled="!metricProfileDraft.name.trim() || !metricProfileDraft.oids.length || creatingMetricProfile" class="btn bg-blue-600 text-white disabled:opacity-50">Create &amp; use profile</button>
            <button type="button" @click="showNewMetricProfileForm = false" class="btn bg-gray-300 text-gray-700 hover:bg-gray-400">Cancel</button>
          </div>
        </div>
      </div>

      <!-- Branch: API -->
      <div v-else-if="method === 'api'" class="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/40 p-4 space-y-4">
        <div class="text-sm font-semibold text-gray-700 dark:text-gray-200">API - query the vendor API</div>
        <div class="flex flex-col">
          <div class="mb-1 flex items-center gap-1.5">
            <label class="font-medium dark:text-white">API credential (token / key)<span class="pl-1 text-red-500">*</span></label>
            <FieldHelp text="Token or key used to authenticate to the vendor API." />
          </div>
          <input id="api-token" v-model="api.token" type="text" placeholder="paste token" @focus="clearFieldError('api_token')" :class="inputClass('api_token')" />
          <FieldError :message="fieldErrors.api_token" />
        </div>
        <div class="flex flex-col">
          <div class="mb-1 flex items-center gap-1.5">
            <label class="font-medium dark:text-white">Base URL / endpoint<span class="pl-1 text-red-500">*</span></label>
            <FieldHelp text="Base URL of the vendor API to query." />
          </div>
          <input id="api-endpoint" v-model="api.endpoint" type="text" placeholder="https://device.mgmt/api" @focus="clearFieldError('api_endpoint')" :class="inputClass('api_endpoint')" />
          <FieldError :message="fieldErrors.api_endpoint" />
        </div>
        <div class="flex flex-col">
          <div class="mb-1 flex items-center gap-1.5">
            <label class="font-medium dark:text-white">Metric groups</label>
            <FieldHelp text="Metric groups to pull from the vendor API." />
          </div>
          <div class="flex gap-2">
            <input v-model="metricInput" type="text" placeholder="add a metric group e.g. Sensor" class="flex-1 px-3 py-2 text-base sm:text-sm rounded-md" @keydown.enter.prevent="addMetric" />
            <button type="button" @click="addMetric" class="btn bg-blue-100 text-blue-700">Add</button>
          </div>
          <div class="flex flex-wrap gap-2 mt-2">
            <div v-for="(metric, index) in api.metrics" :key="metric + index" class="flex items-center gap-1 glass-card-lite rounded px-2 py-0.5">
              <span class="text-sm font-mono">{{ metric }}</span>
              <button type="button" @click="api.metrics.splice(index, 1)" aria-label="Remove metric group" title="Remove metric group"
                class="bg-transparent hover:bg-transparent p-0 text-red-500 hover:text-red-700 dark:hover:text-red-400">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" /></svg>
              </button>
            </div>
          </div>
        </div>
        <FieldError :message="fieldErrors.method_config" />
      </div>

      <!-- Branch: Fabric (FortiGate only) -->
      <div v-else-if="method === 'fabric'" class="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/40 p-4 space-y-4">
        <div class="text-sm font-semibold text-gray-700 dark:text-gray-200">Fortigate Fabric</div>
        <div class="flex flex-col">
          <div class="mb-1 flex items-center gap-1.5">
            <label class="font-medium dark:text-white">Fabric root target (IP / FQDN)<span class="pl-1 text-red-500">*</span></label>
            <FieldHelp text="IP or FQDN of the FortiGate Fabric root." />
          </div>
          <input id="fabric-target" v-model="fabric.endpoint" type="text" placeholder="e.g. 10.0.0.1" @focus="clearFieldError('fabric_target')" :class="inputClass('fabric_target')" />
          <FieldError :message="fieldErrors.fabric_target" />
        </div>
        <div class="flex flex-col">
          <div class="mb-1 flex items-center gap-1.5">
            <label class="font-medium dark:text-white">API token<span class="pl-1 text-red-500">*</span></label>
            <FieldHelp text="Token used to authenticate to the Fabric API." />
          </div>
          <input id="fabric-token" v-model="fabric.credential" type="text" placeholder="paste token" @focus="clearFieldError('fabric_token')" :class="inputClass('fabric_token')" />
          <FieldError :message="fieldErrors.fabric_token" />
        </div>
        <FieldError :message="fieldErrors.method_config" />
      </div>

      <!-- Reachability (independent of the metrics method) -->
      <div class="flex flex-col border-t border-gray-200 dark:border-gray-700 pt-5">
        <div class="flex items-center gap-1.5">
          <label id="monitor-reachability-toggle" class="flex w-fit items-center gap-2 font-medium dark:text-white">
            <input type="checkbox" v-model="reachability.enabled" class="h-5 w-5" @change="onReachabilityToggle" /> Monitor reachability
          </label>
          <FieldHelp text="Check that the device is reachable, independent of metrics collection." />
        </div>
        <div v-if="reachability.enabled" class="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div class="flex flex-col">
            <div class="mb-1 flex items-center gap-1.5">
              <label class="font-medium dark:text-white">Protocol<span class="pl-1 text-red-500">*</span></label>
              <FieldHelp text="Reachability check protocol: ICMP or TCP Connect." />
            </div>
            <GlassSelect id="reachability_protocol" v-model="reachability.protocol" :options="reachabilityProtocolOptions" :valueKey="'value'" :labelKey="'label'" placeholder="Select a Protocol" :error="!!fieldErrors.reachability_protocol" @change="clearFieldError('reachability_protocol')" />
            <FieldError :message="fieldErrors.reachability_protocol" />
          </div>
          <div v-if="reachability.protocol === 'tcp'" class="flex flex-col">
            <div class="mb-1 flex items-center gap-1.5">
              <label class="font-medium dark:text-white">TCP port<span class="pl-1 text-red-500">*</span></label>
              <FieldHelp text="Port to test for TCP Connect reachability." />
            </div>
            <input id="reachability_port" v-model.number="reachability.port" type="number" min="1" max="65535" placeholder="e.g. 443" @focus="clearFieldError('reachability_port')" :class="inputClass('reachability_port')" />
            <FieldError :message="fieldErrors.reachability_port" />
          </div>
        </div>
      </div>

      <div class="flex justify-end space-x-2 border-t border-gray-200 dark:border-gray-700 pt-5">
        <button v-if="!isMultiSubmit" type="button" @click="goBack" class="btn-cancel">Cancel</button>
        <button v-else type="button" @click="skipMultiSubmit" class="btn bg-gray-300 text-gray-700 rounded hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 transition duration-200">Skip</button>
        <button id="submit-network-device-btn" type="submit" :disabled="tutorialActive" :class="tutorialActive ? 'opacity-50 cursor-not-allowed' : ''" class="btn bg-blue-600 text-white hover:bg-blue-700 focus:ring-2 focus:ring-blue-500">{{ isEdit ? 'Save changes' : 'Add Network Device' }}</button>
      </div>
    </form>
  </div>

  <PageError v-else-if="permsLoaded && !canSubmit && !user?.is_superuser" :error="errorMessages['403']" :errorCode="'403'" />
</template>

<script>
import { createNetworkDevice, updateNetworkDevice, getNetworkDeviceById, getTechTypes, getDeviceTypes } from "@/services/networkDeviceService";
import { getSites } from "@/services/siteService";
import { getAllProbes } from "@/services/probeService";
import { getAllSnmpAuthProfiles, createSnmpAuthProfile } from "@/services/snmpAuthProfileService";
import { getAllSNMPProfiles, getSNMPProfileById, createSNMPProfile, createSNMPWalk, deleteSNMPProfile } from "@/services/snmpProfileService";
import GlassSelect from "@/components/GlassSelect.vue";
import FieldError from "@/components/FieldError.vue";
import FieldHelp from "@/components/FieldHelp.vue";
import PageError from "@/components/PageError.vue";
import { useStore } from "@/store";
import { errorMessages, extractErrorMessage } from "@/utils/errorMessages";
import { isValidIpOrFqdn, isValidUrl } from "@/utils/networkValidation";
import { methodLabel, REACHABILITY_PROTOCOL_LABELS } from "@/utils/deviceMonitoring";
import { useMultiTenant } from "@/composables/useMultiTenant";
import { useTutorial } from "@/composables/tutorial";

const blankProfileDraft = () => ({ name: "", ver: 2, community: "", username: "", security_level: "authPriv", auth_protocol: "SHA", auth_password: "", priv_protocol: "AES", priv_password: "" });

// Id for the placeholder records the tutorial injects; never a real row.
const DEMO_ID = -1;

// Per-device form state, reset whenever the route switches between add and
// edit (the two routes share this component, so the instance is reused).
const blankDeviceFormState = () => ({
  anchor: "site",
  tech: "",
  method: "",
  savedMethod: "",
  metricsEnabled: false,
  form: { name: "", target: "", device_type: "", site: "", probe: "", scrape_interval: 120, scrape_timeout: 60, in_maintenance: false },
  snmp: { authProfile: "", metricProfile: "" },
  metricProfileDraft: { name: "", oids: [] },
  selectedProfileOids: [],
  showNewMetricProfileForm: false,
  showNewAuthProfileForm: false,
  api: { token: "", endpoint: "", metrics: [] },
  fabric: { endpoint: "", credential: "" },
  reachability: { enabled: false, protocol: "", port: "" },
  oidInput: "",
  metricInput: "",
  profileDraft: blankProfileDraft(),
  error: "",
  fieldErrors: {},
});

export default {
  name: "AddNetworkDevice",
  components: { GlassSelect, FieldError, FieldHelp, PageError },
  emits: ["network-device-submitted", "network-device-skipped"],
  props: {
    isMultiSubmit: { type: Boolean, default: false },
    submittedSite: { type: Object, default: null },
    submittedProbe: { type: Object, default: null },
  },
  setup() {
    const { start } = useTutorial();
    return { tenant: useMultiTenant(), _start: start };
  },
  data() {
    return {
      techTypes: {},
      deviceTypes: [],
      loading: true,
      tutorialActive: false,
      errorMessages,
      sites: [], probes: [], authProfiles: [],
      ...blankDeviceFormState(),
      metricProfiles: [],
      profileWalksCache: {},
      creatingMetricProfile: false,
      reachabilityProtocolOptions: Object.entries(REACHABILITY_PROTOCOL_LABELS).map(([value, label]) => ({ value, label })),
      showSecrets: false,
      creatingProfile: false,
      secLevelOptions: ["noAuthNoPriv", "authNoPriv", "authPriv"].map((option) => ({ value: option, label: option })),
      authProtoOptions: ["MD5", "SHA", "SHA224", "SHA256", "SHA384", "SHA512"].map((option) => ({ value: option, label: option })),
      privProtoOptions: ["DES", "AES", "AES192", "AES256"].map((option) => ({ value: option, label: option })),
      addNetworkDeviceTutorialSteps: [
        {
          element: '#name',
          action: 'type',
          value: 'core-switch-01',
          message: 'Enter a name for the network device.'
        },
        {
          element: '#target',
          action: 'type',
          value: '10.0.0.1',
          message: 'Enter the target destination: an IP address or FQDN.'
        },
        {
          element: '#site',
          action: 'display',
          message: 'Anchor the device to a site, or use the toggle above to anchor it to a probe instead.'
        },
        {
          element: '#device_type',
          action: 'display',
          message: 'Select the device type (switch, router, firewall, etc.).'
        },
        {
          element: '#tech',
          action: 'display',
          message: 'Select the tech type. This determines which metrics methods are available.'
        },
        {
          element: '#collect-metrics-toggle',
          action: 'display',
          message: 'Metrics collection is enabled here. Choose how metrics are gathered below.'
        },
        {
          element: '#metrics-method-group',
          action: 'display',
          message: 'Pick a metrics method. The options depend on the tech type: SNMP, API, or Fabric. Each choice reveals its own configuration section.'
        },
        {
          element: '#method-snmp',
          action: 'click',
          message: 'SNMP polls the device MIBs. Selecting a method opens its own configuration below.'
        },
        {
          element: '#snmp-auth-profile',
          action: 'display',
          message: 'Choose the SNMP auth profile (v2c community or v3 credentials), or create one with + New.'
        },
        {
          element: '#snmp-metric-profile',
          action: 'display',
          message: 'Choose the metric profile - the set of walk-OIDs to collect - or create one with + New.'
        },
        {
          element: '#method-api',
          action: 'click',
          message: 'API collects metrics from the vendor API instead. Selecting it swaps in the API configuration.'
        },
        {
          element: '#api-token',
          action: 'type',
          value: 'tutorial-token',
          message: 'Paste the API credential (token or key).'
        },
        {
          element: '#api-endpoint',
          action: 'type',
          value: 'https://device.mgmt/api',
          message: 'Enter the base URL of the vendor API.'
        },
        {
          element: '#method-fabric',
          action: 'click',
          message: 'Fabric uses the FortiGate Security Fabric. Selecting it swaps in the Fabric configuration.'
        },
        {
          element: '#fabric-target',
          action: 'type',
          value: '10.0.0.1',
          message: 'Enter the Fabric root target (IP or FQDN).'
        },
        {
          element: '#fabric-token',
          action: 'type',
          value: 'tutorial-token',
          message: 'Enter the Fabric API token.'
        },
        {
          element: '#monitor-reachability-toggle',
          action: 'display',
          message: 'Reachability monitoring is enabled here, independently of metrics collection.'
        },
        {
          element: '#reachability_protocol',
          action: 'select',
          value: 'tcp',
          message: 'Choose the reachability protocol: ICMP or TCP Connect. TCP Connect also needs a port.'
        },
        {
          element: '#reachability_port',
          action: 'type',
          value: '443',
          message: 'Enter the TCP port to test the connection against.'
        },
        {
          element: '#submit-network-device-btn',
          action: 'display',
          message: 'Click to save the network device.'
        },
      ],
    };
  },
  computed: {
    user() { return useStore.getUser(); },
    permsLoaded() { return useStore.permissionsLoadedRef.value; },
    deviceId() { return this.$route.params.id || null; },
    isEdit() { return !!this.deviceId; },
    canAddNetworkDevice() { return useStore.hasPermission("Can add Network Device"); },
    canChangeNetworkDevice() { return useStore.hasPermission("Can change Network Device"); },
    canSubmit() { return this.isEdit ? this.canChangeNetworkDevice : this.canAddNetworkDevice; },
    techOptions() {
      return Object.values(this.techTypes).map((tech) => ({ value: tech.value, label: tech.label }));
    },
    supportedMethods() { return this.techTypes[this.tech]?.methods || []; },
    showAnchorToggle() { return !this.isMultiSubmit || !!this.submittedProbe; },
    authProfileOptions() {
      return [
        ...this.authProfiles.map((profile) => ({ value: profile.id, label: profile.name })),
      ];
    },
    metricProfileOptions() {
      return [
        ...this.metricProfiles.map((profile) => ({ value: profile.id, label: profile.name })),
      ];
    },
    anchorOrg() {
      const anchorList = this.anchor === "site" ? this.sites : this.probes;
      const anchorId = this.anchor === "site" ? this.form.site : this.form.probe;
      return anchorList.find((entry) => entry.id === anchorId)?.org ?? null;
    },
  },
  watch: {
    "$route.params.id"() {
      if (this.$route.name !== "AddNetworkDevice" && this.$route.name !== "EditNetworkDevice") return;
      Object.assign(this, blankDeviceFormState());
      this.initializeForm();
    },
  },
  async mounted() {
    await this.initializeForm();
  },
  methods: {
    async startTutorial() {
      this.tutorialActive = true;
      const snapshot = {
        anchor: this.anchor,
        sites: [...this.sites],
        authProfiles: [...this.authProfiles],
        metricProfiles: [...this.metricProfiles],
        form: { ...this.form },
        tech: this.tech,
        method: this.method,
        metricsEnabled: this.metricsEnabled,
        snmp: { ...this.snmp },
        api: { ...this.api, metrics: this.api.metrics.slice() },
        fabric: { ...this.fabric },
        reachability: { ...this.reachability },
        selectedProfileOids: this.selectedProfileOids.slice(),
        metricProfileDraft: { name: this.metricProfileDraft.name, oids: this.metricProfileDraft.oids.slice() },
        profileWalksCache: { ...this.profileWalksCache },
      };
      // Set directly rather than clicking the checkboxes: a programmatic click
      // does not reliably flip a v-model checkbox in Firefox.
      this.method = "";
      this.reachability.protocol = "";
      this.metricsEnabled = true;
      this.reachability.enabled = true;
      // Demo records for the org-scoped dropdowns so the walkthrough never
      // selects a real site or profile. Restored on close.
      this.anchor = "site";
      this.sites = [{ id: DEMO_ID, name: "Example Site" }];
      this.authProfiles = [{ id: DEMO_ID, name: "Example Auth Profile" }];
      this.metricProfiles = [{ id: DEMO_ID, name: "Example Metric Profile" }];
      this.profileWalksCache[DEMO_ID] = []; // skip the getSNMPProfileById fetch
      // Most-methods tech so the SNMP/API/Fabric walkthrough can show each one.
      const richestTech = Object.values(this.techTypes).reduce(
        (best, candidate) =>
          (candidate.methods?.length || 0) > (best?.methods?.length || 0) ? candidate : best,
        null
      );
      const tutorialTechValue = richestTech?.value ?? this.techOptions[0]?.value;
      const tutorialDropdownValues = {
        '#site': DEMO_ID,
        '#device_type': this.deviceTypes[0]?.value,
        '#tech': tutorialTechValue,
        '#snmp-auth-profile': DEMO_ID,
        '#snmp-metric-profile': DEMO_ID,
      };
      const steps = this.addNetworkDeviceTutorialSteps.map((step) => {
        const dropdownValue = tutorialDropdownValues[step.element];
        return dropdownValue !== undefined
          ? { ...step, action: 'select', value: dropdownValue }
          : step;
      });
      await this._start(steps, () => {
        this.tutorialActive = false;
        this.anchor = snapshot.anchor;
        this.sites = snapshot.sites;
        this.authProfiles = snapshot.authProfiles;
        this.metricProfiles = snapshot.metricProfiles;
        this.form = { ...snapshot.form };
        this.tech = snapshot.tech;
        this.method = snapshot.method;
        this.metricsEnabled = snapshot.metricsEnabled;
        this.snmp = { ...snapshot.snmp };
        this.api = { ...snapshot.api };
        this.fabric = { ...snapshot.fabric };
        this.reachability = { ...snapshot.reachability };
        this.selectedProfileOids = snapshot.selectedProfileOids;
        this.metricProfileDraft = snapshot.metricProfileDraft;
        this.profileWalksCache = snapshot.profileWalksCache;
      });
    },
    async initializeForm() {
      this.loading = true;
      if (this.isMultiSubmit) this.anchor = "site";
      const validation = this.tenant.validateSchemaForOperation();
      const client = validation.valid ? validation.client : null;
      try {
        await Promise.all([
          this.loadDropdowns(client),
          this.isEdit ? this.loadDevice(client) : Promise.resolve(),
        ]);
      } catch (err) {
        console.error("Error initializing network device form:", err);
        this.error = "Could not load the form. Please refresh and try again.";
      } finally {
        this.loading = false;
      }
    },
    async loadDropdowns(client) {
      const [techTypeList, deviceTypes, sites, probes, authProfiles, metricProfiles] = await Promise.all([
        getTechTypes(client),
        getDeviceTypes(client),
        getSites(client),
        getAllProbes(client),
        getAllSnmpAuthProfiles(client),
        getAllSNMPProfiles(client),
      ]);
      this.techTypes = Object.fromEntries(techTypeList.map((techType) => [techType.value, techType]));
      this.deviceTypes = deviceTypes;
      this.sites = sites;
      this.probes = probes;
      this.authProfiles = authProfiles;
      this.metricProfiles = metricProfiles;
      if (this.isMultiSubmit && this.submittedSite) {
        this.sites = [this.submittedSite];
        this.form.site = this.submittedSite.id;
        if (this.submittedProbe) {
          this.probes = [this.submittedProbe];
          this.form.probe = this.submittedProbe.id;
        }
      }
    },
    async loadDevice(client) {
      const device = await getNetworkDeviceById(this.deviceId, client);
      this.form.name = device.name;
      this.form.target = device.target;
      this.form.device_type = device.device_type;
      this.form.scrape_interval = device.scrape_interval;
      this.form.scrape_timeout = device.scrape_timeout;
      this.form.in_maintenance = device.in_maintenance;
      this.anchor = device.site ? "site" : "probe";
      this.form.site = device.site || "";
      this.form.probe = device.probe || "";
      this.tech = device.tech_type || "";
      this.method = device.method || "";
      this.savedMethod = device.method || "";
      this.metricsEnabled = !!device.method;
      this.snmp.authProfile = device.auth || "";
      this.reachability.enabled = device.reachability || false;
      this.reachability.protocol = device.reachability_protocol || "";
      this.reachability.port = device.reachability_port || "";
      const config = device.method_config || {};
      if (device.method === "snmp") {
        this.snmp.metricProfile = device.snmp_metric_profile || "";
        await this.loadSelectedProfileOids();
      } else if (device.method === "api") {
        this.api.token = config.token || "";
        this.api.endpoint = config.endpoint || "";
        this.api.metrics = Array.isArray(config.metrics) ? config.metrics.slice() : [];
      } else if (device.method === "fabric") {
        this.fabric.endpoint = config.target || "";
        this.fabric.credential = config.token || "";
      }
    },
    inputClass(key) {
      return this.fieldErrors[key]
        ? "px-3 py-2 text-base sm:text-sm rounded-md ring-2 ring-red-500 focus:ring-2 focus:ring-red-500"
        : "px-3 py-2 text-base sm:text-sm rounded-md focus:ring-2 focus:ring-blue-500";
    },
    clearFieldError(field) { this.error = ""; if (this.fieldErrors[field]) delete this.fieldErrors[field]; },
    methodLabel,
    walkLabel(walk) {
      return walk.name || walk.oid;
    },
    methodClass(monitoringMethod) {
      return this.method === monitoringMethod
        ? "bg-blue-600 text-white border border-blue-600"
        : "bg-transparent border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-blue-500";
    },
    resetMetricsConfig() {
      const techType = this.techTypes[this.tech] || {};
      this.snmp.authProfile = "";
      this.snmp.metricProfile = "";
      this.selectedProfileOids = [];
      this.showNewAuthProfileForm = false;
      this.showNewMetricProfileForm = false;
      this.metricProfileDraft = { name: "", oids: (techType.default_oids || []).slice() };
      this.api.token = "";
      this.api.endpoint = "";
      this.api.metrics = (techType.default_metric_groups || []).slice();
      this.fabric.endpoint = "";
      this.fabric.credential = "";
    },
    onTechChange() {
      ["tech_type", "method", "auth", "snmp_metric_profile", "api_token", "api_endpoint", "fabric_target", "fabric_token", "method_config"]
        .forEach((key) => this.clearFieldError(key));
      const techType = this.techTypes[this.tech] || {};
      if (this.method && !(techType.methods || []).includes(this.method)) this.method = "";
      this.resetMetricsConfig();
      if (this.method === "snmp") this.preselectBaseProfile();
    },
    selectMethod(monitoringMethod) {
      if (!this.supportedMethods.includes(monitoringMethod)) return;
      this.method = this.method === monitoringMethod ? "" : monitoringMethod;
      const techType = this.techTypes[this.tech] || {};
      if (this.method === "api" && !this.api.metrics.length) this.api.metrics = (techType.default_metric_groups || []).slice();
      if (this.method === "snmp" && !this.snmp.metricProfile) this.preselectBaseProfile();
      this.clearFieldError("method");
      if (this.method !== "snmp") { this.clearFieldError("auth"); this.clearFieldError("snmp_metric_profile"); }
    },
    onMetricsToggle() {
      if (!this.metricsEnabled) {
        this.method = "";
        ["auth", "snmp_metric_profile", "api_token", "api_endpoint", "fabric_target", "fabric_token", "method_config"]
          .forEach((key) => this.clearFieldError(key));
      }
      this.clearFieldError("method");
    },
    onReachabilityToggle() {
      if (!this.reachability.enabled) {
        this.reachability.protocol = "";
        this.reachability.port = "";
        this.clearFieldError("reachability_protocol");
        this.clearFieldError("reachability_port");
      }
      this.clearFieldError("method");
    },
    onAuthChange() {
      this.clearFieldError("auth");
      if (this.snmp.authProfile) this.showNewAuthProfileForm = false;
    },
    openNewAuthProfileForm() {
      this.profileDraft = blankProfileDraft();
      this.showNewAuthProfileForm = true;
      this.clearFieldError("auth");
    },
    openNewMetricProfileForm() {
      const techType = this.techTypes[this.tech] || {};
      this.metricProfileDraft = { name: "", oids: (techType.default_oids || []).slice() };
      this.showNewMetricProfileForm = true;
      this.clearFieldError("snmp_metric_profile");
    },
    preselectBaseProfile() {
      const baseProfile = this.metricProfiles.find((profile) => profile.name === `${this.tech}_default`);
      if (baseProfile) {
        this.snmp.metricProfile = baseProfile.id;
        this.loadSelectedProfileOids();
      }
    },
    onMetricProfileChange() {
      this.clearFieldError("snmp_metric_profile");
      if (this.snmp.metricProfile) this.showNewMetricProfileForm = false;
      this.loadSelectedProfileOids();
    },
    async loadSelectedProfileOids() {
      const profileId = this.snmp.metricProfile;
      if (!profileId) { this.selectedProfileOids = []; return; }
      if (this.profileWalksCache[profileId]) {
        this.selectedProfileOids = this.profileWalksCache[profileId];
        return;
      }
      try {
        const validation = this.tenant.validateSchemaForOperation();
        const profile = await getSNMPProfileById(profileId, validation.valid ? validation.client : null);
        const walks = (profile.walks || []).map((walk) => ({ oid: walk.oid, name: walk.description || "" }));
        this.profileWalksCache[profileId] = walks;
        if (profileId !== this.snmp.metricProfile) return;
        this.selectedProfileOids = walks;
      } catch (err) {
        console.error("Error loading metric profile walks:", err);
        if (profileId === this.snmp.metricProfile) this.selectedProfileOids = [];
      }
    },
    async createMetricProfile() {
      const profileName = this.metricProfileDraft.name.trim();
      if (!profileName || !this.metricProfileDraft.oids.length) return;
      if (this.anchorOrg === null) {
        this.fieldErrors.snmp_metric_profile = "Select a site or probe first so the profile can be scoped to its organisation.";
        return;
      }
      this.creatingMetricProfile = true;
      const validation = this.tenant.validateSchemaForOperation();
      const client = validation.valid ? validation.client : null;
      let created = null;
      try {
        created = await createSNMPProfile({ name: profileName, org: this.anchorOrg }, client);
        await Promise.all(
          this.metricProfileDraft.oids.map((walk) =>
            createSNMPWalk({ profile: created.id, oid: walk.oid, description: walk.name }, client)
          )
        );
        this.metricProfiles.push(created);
        this.snmp.metricProfile = created.id;
        this.showNewMetricProfileForm = false;
        this.selectedProfileOids = this.metricProfileDraft.oids.slice();
        this.profileWalksCache[created.id] = this.selectedProfileOids;
        this.metricProfileDraft = { name: "", oids: [] };
      } catch (err) {
        if (created) {
          try { await deleteSNMPProfile(created.id, client); } catch (cleanupErr) { console.error("Error rolling back metric profile:", cleanupErr); }
        }
        const walkOidError = err?.response?.data?.oid;
        if (walkOidError) this.fieldErrors.walk_oid = Array.isArray(walkOidError) ? walkOidError[0] : walkOidError;
        else this.error = extractErrorMessage(err) || "Could not create the metric profile.";
      } finally { this.creatingMetricProfile = false; }
    },
    addOid() {
      const oid = this.oidInput.trim();
      if (!oid) return;
      this.clearFieldError("walk_oid");
      this.metricProfileDraft.oids.push({ oid, name: "" });
      this.oidInput = "";
    },
    addMetric() { const metric = this.metricInput.trim(); if (metric) { this.api.metrics.push(metric); this.metricInput = ""; } },
    async createProfile() {
      const profileName = this.profileDraft.name.trim();
      if (!profileName) return;
      this.creatingProfile = true;
      const validation = this.tenant.validateSchemaForOperation();
      const payload = { name: profileName, snmp_version: this.profileDraft.ver === 3 ? "v3" : "v2c" };
      if (this.profileDraft.ver === 2) payload.community_string = this.profileDraft.community;
      else Object.assign(payload, {
        username: this.profileDraft.username, security_level: this.profileDraft.security_level,
        auth_protocol: this.profileDraft.auth_protocol, auth_password: this.profileDraft.auth_password,
        priv_protocol: this.profileDraft.priv_protocol, priv_password: this.profileDraft.priv_password,
      });
      try {
        const created = await createSnmpAuthProfile(payload, validation.valid ? validation.client : null);
        this.authProfiles.push(created);
        this.snmp.authProfile = created.id;
        this.showNewAuthProfileForm = false;
        this.profileDraft = blankProfileDraft();
      } catch (err) {
        this.error = extractErrorMessage(err) || "Could not create the auth profile.";
      } finally { this.creatingProfile = false; }
    },
    validateForm() {
      this.fieldErrors = {};
      if (!this.form.name) this.fieldErrors.name = "This field is required.";
      if (!this.form.target) this.fieldErrors.target = "This field is required.";
      if (this.anchor === "site" && !this.form.site) this.fieldErrors.site = "Select a site.";
      if (this.anchor === "probe" && !this.form.probe) this.fieldErrors.probe = "Select a probe.";
      if (!this.tech) this.fieldErrors.tech_type = "Pick a Tech Type.";
      if (!this.form.device_type) this.fieldErrors.device_type = "Pick a Device Type.";
      if (!this.metricsEnabled && !this.reachability.enabled)
        this.fieldErrors.method = "Enable metrics collection, reachability, or both.";
      else if (this.metricsEnabled && !this.method)
        this.fieldErrors.method = "Pick a metrics method.";
      if (this.method === "snmp") {
        if (this.showNewAuthProfileForm)
          this.fieldErrors.auth = "Finish the new auth profile (Create & use) or cancel it.";
        else if (!this.snmp.authProfile)
          this.fieldErrors.auth = "Select an auth profile, or create one.";
        if (this.showNewMetricProfileForm)
          this.fieldErrors.snmp_metric_profile = "Finish the new metric profile (Create & use) or cancel it.";
        else if (!this.snmp.metricProfile)
          this.fieldErrors.snmp_metric_profile = "Select a metric profile, or create one.";
      }
      if (this.method === "api") {
        if (!this.api.token) this.fieldErrors.api_token = "This field is required.";
        if (!this.api.endpoint) this.fieldErrors.api_endpoint = "This field is required.";
        else if (!isValidUrl(this.api.endpoint)) this.fieldErrors.api_endpoint = "Enter a valid URL (http:// or https://).";
      }
      if (this.method === "fabric") {
        if (!this.fabric.endpoint) this.fieldErrors.fabric_target = "This field is required.";
        else if (!isValidIpOrFqdn(this.fabric.endpoint)) this.fieldErrors.fabric_target = "Enter a valid IP address or FQDN.";
        if (!this.fabric.credential) this.fieldErrors.fabric_token = "This field is required.";
      }
      if (this.reachability.enabled) {
        if (!this.reachability.protocol) {
          this.fieldErrors.reachability_protocol = "Choose ICMP or TCP.";
        } else if (this.reachability.protocol === "tcp") {
          const port = Number(this.reachability.port);
          if (!Number.isInteger(port) || port < 1 || port > 65535)
            this.fieldErrors.reachability_port = "Enter a valid TCP port (1-65535).";
        }
      }
      const scrapeInterval = Number(this.form.scrape_interval), scrapeTimeout = Number(this.form.scrape_timeout);
      if (scrapeTimeout > scrapeInterval) this.fieldErrors.scrape_timeout = "Scrape timeout cannot exceed scrape interval.";
      return Object.keys(this.fieldErrors).length === 0;
    },
    methodConfig() {
      if (this.method === "api") return { token: this.api.token, endpoint: this.api.endpoint, metrics: this.api.metrics };
      if (this.method === "fabric") return { target: this.fabric.endpoint, token: this.fabric.credential };
      return {};
    },
    async submitForm() {
      if (this.tutorialActive) return;
      this.error = "";
      if (!this.validateForm()) return;
      const validation = this.tenant.validateSchemaForOperation();
      if (!validation.valid) { this.error = "Malformed request."; return; }
      const payload = {
        name: this.form.name, target: this.form.target,
        device_type: this.form.device_type, tech_type: this.tech, method: this.method || null,
        auth: this.method === "snmp" ? this.snmp.authProfile : null,
        snmp_metric_profile: this.method === "snmp" ? this.snmp.metricProfile : null,
        method_config: this.methodConfig(),
        reachability: this.reachability.enabled,
        reachability_protocol: this.reachability.enabled ? this.reachability.protocol : null,
        reachability_port: this.reachability.enabled && this.reachability.protocol === "tcp" ? this.reachability.port : null,
        in_maintenance: this.form.in_maintenance,
      };
      if (useStore.isStaff()) {
        payload.scrape_interval = this.form.scrape_interval;
        payload.scrape_timeout = this.form.scrape_timeout;
      }
      if (this.anchor === "site") { payload.site = this.form.site; payload.probe = null; }
      else { payload.probe = this.form.probe; payload.site = null; }
      try {
        if (this.isEdit) {
          await updateNetworkDevice(this.deviceId, payload, validation.client);
          this.goBack();
        } else {
          const created = await createNetworkDevice(payload, validation.client);
          if (this.isMultiSubmit) this.$emit("network-device-submitted", created);
          else this.goBack();
        }
      } catch (err) {
        this.fieldErrors = {};
        const response = err?.response;
        const errData = response?.data;
        const isValidationError =
          response?.status === 400 &&
          errData && typeof errData === "object" && !Array.isArray(errData);
        if (isValidationError) {
          const visibleFieldKeys = new Set(["name", "target", "site", "probe", "auth", "snmp_metric_profile", "scrape_timeout", "tech_type", "device_type", "method", "method_config", "reachability_protocol", "reachability_port"]);
          const generalMessages = [];
          Object.entries(errData).forEach(([key, value]) => {
            const raw = Array.isArray(value) ? value[0] : value;
            if (!raw) return;
            const message = typeof raw === "string" ? raw : JSON.stringify(raw);
            if (visibleFieldKeys.has(key)) this.fieldErrors[key] = message;
            else generalMessages.push(message);
          });
          if (generalMessages.length) {
            this.error = generalMessages.join(" ");
          }
        } else if (response) {
          this.error = extractErrorMessage(err, "Failed to create the network device.");
        } else {
          this.error = "Network error or server unreachable. Please try again.";
        }
        console.error("Error creating network device:", err);
      }
    },
    goBack() { this.$router.push({ name: "NetworkDevices", query: this.tenant.addSchemaParam() }); },
    skipMultiSubmit() { this.$emit("network-device-skipped", "skipped"); },
  },
};
</script>
